import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { SeoItem } from '../types';

type Action = 'approve' | 'reject' | 'changes' | 'undo';

interface Props {
  item: SeoItem;
  // What "apply" and "undo" do, in the dialogs' words.
  applyText: string;
  undoText: string;
  onApprove: (item: SeoItem) => Promise<void>;
  onReject: (item: SeoItem, reason: string) => Promise<void>;
  onRequestChanges: (item: SeoItem, feedback: string) => Promise<void>;
  onUndo: (item: SeoItem) => Promise<void>;
}

// Buttons for a change to an existing article: Apply / Request changes /
// Reject while it waits, Undo while it's live.
export default function ChangeActions({
  item,
  applyText,
  undoText,
  onApprove,
  onReject,
  onRequestChanges,
  onUndo,
}: Props) {
  const [open, setOpen] = React.useState<Action | null>(null);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setOpen(null);
    setText('');
    setError(null);
  }, [item.id]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (open === 'approve') await onApprove(item);
      if (open === 'reject') await onReject(item, text);
      if (open === 'changes') await onRequestChanges(item, text);
      if (open === 'undo') await onUndo(item);
      setOpen(null);
      setText('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {item.status === 'proposed' && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="success"
            onClick={() => setOpen('approve')}
          >
            Apply
          </Button>
          <Button variant="outlined" onClick={() => setOpen('changes')}>
            Request changes
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpen('reject')}
          >
            Reject
          </Button>
        </Stack>
      )}
      {item.status === 'published' && (
        <Box>
          <Button variant="outlined" onClick={() => setOpen('undo')}>
            Undo this change
          </Button>
        </Box>
      )}

      <Dialog
        open={open !== null}
        onClose={() => !busy && setOpen(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {open === 'approve' && 'Apply this change?'}
          {open === 'changes' && 'What should change?'}
          {open === 'reject' && 'Reject this change?'}
          {open === 'undo' && 'Undo this change?'}
        </DialogTitle>
        <DialogContent>
          {open === 'approve' && (
            <DialogContentText>{applyText}</DialogContentText>
          )}
          {open === 'undo' && <DialogContentText>{undoText}</DialogContentText>}
          {(open === 'changes' || open === 'reject') && (
            <TextField
              id="seo-change-text"
              autoFocus
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1 }}
              label={open === 'changes' ? 'Feedback' : 'Reason (optional)'}
              value={text}
              onChange={e => setText(e.target.value)}
            />
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color={open === 'reject' ? 'error' : 'primary'}
            loading={busy}
            disabled={open === 'changes' && text.trim().length < 3}
            onClick={submit}
          >
            {open === 'approve' && 'Apply'}
            {open === 'changes' && 'Send back'}
            {open === 'reject' && 'Reject'}
            {open === 'undo' && 'Undo'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
